// Session-scoped record of URLs that have finished decoding successfully — cheap
// presence flags, kept for the life of the session so revisiting a screen never
// re-warms the same bytes.
const warmed = new Set<string>()

// Promises for decodes still in flight, keyed by URL. A second, concurrent caller
// for the same in-flight URL awaits this shared promise instead of racing ahead of
// a decode that has merely started — it resolves only once the bytes have actually
// decoded (or failed), i.e. it records *completion*, not initiation. Self-cleaning:
// each entry is removed once its decode settles, so this only ever holds Promises
// for genuinely pending work, not an ever-growing session-lifetime cache.
const inFlight = new Map<string, Promise<void>>()

// A bare URL, or the `src`/`srcSet` pair an <img> renders. Pass the pair whenever
// the <img> has a srcset: the browser then picks the same density candidate here
// as it will on screen, so the warmed bytes are the ones the <img> asks for.
export type PreloadImageSource = string | { src: string; srcSet?: string }

type PreloadImagesOptions = {
  // 'low' for speculative warming (the PDP prefetching another screen's tiles), so
  // it never competes with the images the user is looking at.
  priority?: 'low' | 'auto'
}

function sourceKey(source: PreloadImageSource): string {
  if (typeof source === 'string') {
    return source
  }

  return source.srcSet || source.src
}

// Decode the given images off-screen and resolve once every one has settled —
// decoded successfully or failed. A 404 rejects `decode()`, so we swallow it: one
// broken image must never stall the reveal that waits on this promise. The source
// passed here must match what the <img> renders (same `src` and `srcSet`), so the
// decoded bytes are a guaranteed cache hit when the grid paints.
export function preloadImages(
  sources: PreloadImageSource[],
  { priority = 'auto' }: PreloadImagesOptions = {},
): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }

  // Dedupe within the call as well as across the session — the above-fold list can
  // repeat a URL, and reusing the shared decode promise below covers the rest.
  const unique = [
    ...new Map(
      sources
        .filter((source) => Boolean(sourceKey(source)))
        .map((source) => [sourceKey(source), source]),
    ).entries(),
  ]

  const pending = unique.map(([key, source]) => {
    if (warmed.has(key)) {
      return Promise.resolve()
    }

    const existingDecode = inFlight.get(key)
    if (existingDecode) {
      return existingDecode
    }

    const image = new Image()
    image.decoding = 'async'
    image.fetchPriority = priority

    if (typeof source === 'string') {
      image.src = source
    }

    if (typeof source !== 'string') {
      // srcset before src, so the browser selects a candidate rather than
      // starting on the 1x `src` first.
      image.srcset = source.srcSet ?? ''
      image.src = source.src
    }

    const decoded = image
      .decode()
      .then(() => {
        warmed.add(key)
      })
      .catch(() => {
        // A transient failure (404, dropped connection) must not poison the URL
        // for the rest of the session — leave it out of `warmed` so a later call
        // re-attempts the decode.
      })
      .finally(() => {
        inFlight.delete(key)
      })

    inFlight.set(key, decoded)

    return decoded
  })

  return Promise.all(pending).then(() => undefined)
}

// Synchronous read of the same session record `preloadImages` writes. A caller
// that already warmed these bytes — the PDP prefetching the discovery grids, say
// — can then skip its loading state on the first render rather than waiting a
// tick for a decode that has already happened.
export function areImagesWarm(sources: PreloadImageSource[]): boolean {
  const keys = sources.map(sourceKey).filter(Boolean)

  if (keys.length === 0) {
    return false
  }

  return keys.every((key) => warmed.has(key))
}

// Record that an <img> on screen has finished loading this source, so the next
// mount of the same source (a screen the user navigates back to, a tile grid
// that re-enters) can skip its skeleton and fade: `areImagesWarm` answers yes.
// Only successful loads are recorded — a broken image must keep its error path.
export function markImagesWarm(sources: PreloadImageSource[]): void {
  for (const key of sources.map(sourceKey)) {
    if (key) {
      warmed.add(key)
    }
  }
}

// Forget every warm source. Tests share one module-level record, so each test
// starts from a cold session.
export function clearWarmedImages(): void {
  warmed.clear()
}
